import { useCallback, useEffect, useRef } from 'react';
import { isUnderwritingAnalysisRequiredError } from '@/features/quotes/hooks/business/use-calculate-quote.hook';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useCalculateCapitalGlobalQuote } from '@/features/quotes/hooks/capital-global/use-calculate-capital-global-quote.hook';
import { useUpdateCapitalGlobalQuote } from '@/features/quotes/hooks/capital-global/use-update-capital-global-quote.hook';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import {
  parsePercent,
  validateCommercialLimit,
  type CommercializationPercentValues,
} from '@/features/quotes/schemas/commercialization-percent.schema';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';

const COMMERCIALIZATION_STEP_CODE = 'COMMERCIALIZATION';

type StepCommercializationFormApi = {
  state: { values: CommercializationPercentValues };
  setFieldValue: (field: string, value: unknown) => void;
  validateAllFields: (cause: string) => Promise<unknown>;
};

type Params = {
  form: unknown;
  onNext: () => void;
};

type Result = {
  handleSubmit: () => Promise<void>;
  isPending: boolean;
  isCalculating: boolean;
};

export function useStepCommercializationViewModel({
  form,
  onNext,
}: Params): Result {
  const formApi = form as StepCommercializationFormApi;

  const { id, policyId } = useQuoteParams();
  const resolvedPolicyId =
    (id && id.trim()) || (policyId && policyId.trim()) || '';
  const hasPolicyId = Boolean(resolvedPolicyId);

  const { mutateAsync, isPending } = useUpdateCapitalGlobalQuote();
  const { mutateAsync: calculateQuote, isPending: isCalculating } =
    useCalculateCapitalGlobalQuote();

  const { data: proposalDetails } = useProposalDetails(
    hasPolicyId ? resolvedPolicyId : undefined
  );

  const prefillAppliedRef = useRef(false);

  /**
   * Hidrata os percentuais já salvos (o back devolve em `commercialization`).
   *
   * Corretagem zerada = step ainda NÃO preenchido: numa cotação nova o resolver
   * já cria os agentes com `CommissionRate: 0`, e o back devolve "0,00" em vez
   * de omitir. Hidratar esse zero deixava o campo inválido antes de o corretor
   * digitar qualquer coisa — `setFieldValue` dispara o validador `onChange`.
   */
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const saved = proposalDetails?.commercialization;
    if (!saved) return;
    if (formApi.state.values.brokeragePercent) return;

    const brokerage = saved.brokeragePercent?.trim();
    if (!brokerage || parsePercent(brokerage) <= 0) return;

    formApi.setFieldValue('brokeragePercent', brokerage);
    formApi.setFieldValue('agencingPercent', saved.agencingPercent?.trim() ?? '');
    prefillAppliedRef.current = true;
  }, [proposalDetails, formApi]);

  const handleSubmit = useCallback(async () => {
    const fieldErrors = await formApi.validateAllFields('change');
    const values = formApi.state.values;
    const limitError = validateCommercialLimit(
      values.brokeragePercent,
      values.agencingPercent
    );
    const hasFieldErrors =
      fieldErrors && Object.keys(fieldErrors as object).length > 0;
    if (hasFieldErrors || limitError) return;

    if (!resolvedPolicyId) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description: 'ID da cotação não encontrado. Por favor, tente novamente.',
      });
      return;
    }

    try {
      const result = await mutateAsync({
        id: resolvedPolicyId,
        payload: {
          step: COMMERCIALIZATION_STEP_CODE,
          brokeragePercent: parsePercent(values.brokeragePercent),
          agencyingPercent: parsePercent(values.agencingPercent),
        },
      });

      if (!result.success) return;

      try {
        await calculateQuote({ policyId: resolvedPolicyId });
      } catch (error) {
        if (isUnderwritingAnalysisRequiredError(error)) {
          onNext();
          return;
        }
        throw error;
      }

      onNext();
    } catch (error) {
      const description =
        error instanceof ValidationError
          ? error.detail?.trim() ||
            (error.messages.length ? error.messages.join('\n') : error.message)
          : error instanceof Error
            ? error.message
            : 'Erro inesperado ao salvar comercialização';

      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar',
        description,
      });
    }
  }, [calculateQuote, formApi, mutateAsync, onNext, resolvedPolicyId]);

  return { handleSubmit, isPending, isCalculating };
}
